import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Model3DViewer from '../components/Model3DViewer';
import apiService from '../services/api';
import { saveOrderId } from '../components/OrderTracker';   // ← order tracking
import '../styles/itemdetails.css';

const TABLE_KEY    = 'tableNumber';
const CART_KEY     = 'cartItems';
const CHANGE_TABLE = '__change_table__';

// ─── SessionStorage helpers ──────────────────────────────────────────
const getCart  = () => JSON.parse(sessionStorage.getItem(CART_KEY) || '[]');
const saveCart = (cart) => sessionStorage.setItem(CART_KEY, JSON.stringify(cart));

// ─── Read restaurantData from sessionStorage ─────────────────────────
const getRestaurantData = () => {
  try {
    return JSON.parse(sessionStorage.getItem('restaurantData') || '{}');
  } catch {
    return {};
  }
};

// plan === "pro" → ordering enabled; anything else → hidden
const isPro = () => (getRestaurantData()?.plan || '').toLowerCase() === 'pro';

export default function ItemDetails() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const location     = useLocation();

  // ── Core state ───────────────────────────────────────────────────
  const [item, setItem]         = useState(location.state?.item || null);
  const [show3D, setShow3D]     = useState(false);
  const [quantity, setQuantity] = useState(1);

  // ── Plan ─────────────────────────────────────────────────────────
  const [proPlan] = useState(isPro);

  // ── Cart state ───────────────────────────────────────────────────
  const [cart, setCart]                 = useState(getCart);
  const [showCart, setShowCart]         = useState(false);
  const [addedFlash, setAddedFlash]     = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);

  // ── Table modal state ────────────────────────────────────────────
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableInput, setTableInput]         = useState('');
  const [tableError, setTableError]         = useState('');
  const [afterTable, setAfterTable]         = useState(null);

  // ── Scroll to top ────────────────────────────────────────────────
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  // ── Fetch item ───────────────────────────────────────────────────
  const fetchItemDetails = useCallback(async () => {
    try {
      const data = await apiService.getItemDetails(id);
      setItem(data);
    } catch (err) {
      console.error('Error loading item details:', err);
    }
  }, [id]);

  useEffect(() => {
    if (!item) fetchItemDetails();
  }, [item, fetchItemDetails]);

  // ── Sync cart → sessionStorage ───────────────────────────────────
  useEffect(() => { saveCart(cart); }, [cart]);

  // ── Quantity helpers ─────────────────────────────────────────────
  const decrement = () => setQuantity((q) => Math.max(1, q - 1));
  const increment = () => setQuantity((q) => q + 1);

  // ── Derived ──────────────────────────────────────────────────────
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  // ── Add to cart ──────────────────────────────────────────────────
  const addToCart = () => {
    if (!item) return;
    setCart((prev) => {
      const existing = prev.find((c) => c.itemId === item.itemId);
      if (existing) {
        return prev.map((c) =>
          c.itemId === item.itemId ? { ...c, qty: c.qty + quantity } : c
        );
      }
      return [
        ...prev,
        { itemId: item.itemId, name: item.name, price: item.price, imageUrl: item.imageUrl, qty: quantity },
      ];
    });
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 1800);
  };

  // ── Cart qty change ──────────────────────────────────────────────
  const changeCartQty = (itemId, delta) => {
    setCart((prev) =>
      prev.map((c) => c.itemId === itemId ? { ...c, qty: c.qty + delta } : c)
          .filter((c) => c.qty > 0)
    );
  };

  // ── Ensure table → callback ──────────────────────────────────────
  const withTable = (fn) => {
    const saved = sessionStorage.getItem(TABLE_KEY);
    if (saved) { fn(saved); return; }
    setTableInput('');
    setTableError('');
    setAfterTable(() => fn);
    setShowTableModal(true);
  };

  // ── Confirm table ────────────────────────────────────────────────
  const handleTableConfirm = () => {
    const val = tableInput.trim();
    if (!val) { setTableError('Please enter your table number.'); return; }
    sessionStorage.setItem(TABLE_KEY, val);
    setShowTableModal(false);
    if (afterTable === CHANGE_TABLE) {
      setAfterTable(null);
      setShowCart(true);
      return;
    }
    if (afterTable) { afterTable(val); setAfterTable(null); }
  };

  // ── restaurantId ─────────────────────────────────────────────────
  const restaurantId = getRestaurantData()?.id;

  // ── Place order ──────────────────────────────────────────────────
  const placeOrder = async (tableNumber) => {
    setPlacingOrder(true);
    try {
      const result = await apiService.placeOrder({ restaurantId, tableNumber, cart });
      if (result.success) {
        // ✅ Save orderId → Navbar tracker will pick it up
        saveOrderId(result.orderId);

        setCart([]);
        sessionStorage.removeItem(CART_KEY);
        setShowCart(false);
        setOrderSuccess(true);
        setTimeout(() => setOrderSuccess(false), 3000);
      }
    } catch (e) {
      console.error('placeOrder error:', e);
    } finally {
      setPlacingOrder(false);
    }
  };

  const handlePlaceOrder = () => {
    if (cart.length === 0) return;
    withTable(placeOrder);
  };

  // ── Loading ──────────────────────────────────────────────────────
  if (!item) {
    return (
      <div className="mobile-container" style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100vh' }}>
        <p style={{ color:'#888', fontSize:16 }}>Loading…</p>
      </div>
    );
  }

  const itemTotal  = (item.price * quantity).toFixed(2);
  const savedTable = sessionStorage.getItem(TABLE_KEY);

  // ═════════════════════════════════════════════════════════════════
  return (
    <div>

      {/* ── SCROLL AREA ──────────────────────────────────────────── */}
      <div className="mobile-container" style={{ paddingBottom: proPlan ? 100 : 32 }}>

        {/* HEADER */}
        <div style={{ display:'flex', alignItems:'center' }}>
          <button className="icon-btn" onClick={() => navigate(-1)}>←</button>
          <div className="top-bar" style={{ alignSelf:'center', display:'flex', justifyContent:'center', width:'100%' }}>
            <h3 style={{ margin:0 }} className="topname">details</h3>
          </div>
          {proPlan && cartCount > 0 && (
            <button style={S.headerCartBtn} onClick={() => setShowCart(true)}>
              🛒
              <span style={S.headerCartBadge}>{cartCount}</span>
            </button>
          )}
        </div>

        {/* IMAGE */}
        <div className="image-wrapper">
          <img src={item.imageUrl} alt={item.name} className="food-image1" />
        </div>

        {/* AR / 3D */}
        {item?.modelUrl && (
          <div className="ar-3d-buttons">
            <button className="btn-3d" onClick={() => setShow3D(true)}>
              <span className="material-icons">view_in_ar</span>View 3D
            </button>
            <button className="btn-ar" onClick={() => navigate(`/ar-view/${item.itemId}`, { state:{ modelUrl:item.modelUrl, itemName:item.name } })}>
              <span className="material-icons">camera</span>View in AR
            </button>
          </div>
        )}

        {/* CONTENT */}
        <div className="content-card">
          <div className="title-row">
            <h1 className="food-item">{item.name}</h1>
            <h1 className="item-price1">₹{item.price}</h1>
          </div>

          <h3 style={{ margin:0, fontSize:16 }}>Description</h3>
          <p className="desc">{item.description}</p>

          <div className="meta-card">
            {[
              { icon:'⏱', val:`${item?.prepTime||'-'} mins`, label:'Prep Time' },
              { icon:'🌶', val:item?.type||'N/A',             label:'Type'      },
              { icon:'🔥', val:`${item?.calories??'-'} kcal`, label:'Calories'  },
              { icon:'🏷', val:item?.category||'N/A',         label:'Category'  },
            ].map(({ icon, val, label }) => (
              <div key={label} className="meta-item">
                <span className="meta-icon">{icon}</span>
                <div><p className="meta-value">{val}</p><p className="meta-label">{label}</p></div>
              </div>
            ))}
          </div>

          <div className="ingredients-section">
            <h3 className="section-title">Items in plate</h3>
            {item?.ingredients?.filter((i) => i?.trim()).length > 0 ? (
              item.ingredients.filter((i) => i?.trim()).map((ing, idx) => (
                <p key={idx} className="ingredient-item">• {ing}</p>
              ))
            ) : <p>No items available</p>}
          </div>
        </div>

      </div>

      {/* ── STICKY BOTTOM BAR — PRO ONLY ─────────────────────────── */}
      {proPlan && (
        <div style={S.stickyBar}>
          <div style={S.qtyWrapper}>
            <button style={{ ...S.qtyBtn, opacity: quantity===1 ? 0.35 : 1 }} onClick={decrement}>−</button>
            <span style={S.qtyNum}>{quantity}</span>
            <button style={S.qtyBtn} onClick={increment}>+</button>
          </div>

          <button
            style={{ ...S.addBtn, ...(addedFlash ? S.addBtnFlash : {}) }}
            onClick={addToCart}
          >
            {addedFlash ? '✓  Added to Cart!' : `+ Add  ·  ₹${itemTotal}`}
          </button>

          {cartCount > 0 && (
            <button style={S.viewCartBtn} onClick={() => setShowCart(true)}>
              🛒
              <span style={S.viewCartBadge}>{cartCount}</span>
            </button>
          )}
        </div>
      )}

      {/* ── SUCCESS TOAST ────────────────────────────────────────── */}
      {orderSuccess && (
        <div style={S.toast}>🎉  Order placed successfully!</div>
      )}

      {/* ══════════════════════════════════════════════════════════
           CART BOTTOM SHEET
         ══════════════════════════════════════════════════════════ */}
      {showCart && proPlan && (
        <div style={S.overlay} onClick={() => setShowCart(false)}>
          <div style={S.sheet} onClick={(e) => e.stopPropagation()}>

            <div style={S.sheetHandle} />

            <div style={S.sheetHeader}>
              <h2 style={S.sheetTitle}>Your Order</h2>
              <button style={S.closeBtn} onClick={() => setShowCart(false)}>✕</button>
            </div>

            {savedTable && (
              <div style={S.tableChip}>
                🪑 Table {savedTable}
                <button
                  style={S.tableChipChange}
                  onClick={() => {
                    sessionStorage.removeItem(TABLE_KEY);
                    setTableInput('');
                    setTableError('');
                    setAfterTable(CHANGE_TABLE);
                    setShowCart(false);
                    setShowTableModal(true);
                  }}
                >
                  Change
                </button>
              </div>
            )}

            <div style={S.cartList}>
              {cart.length === 0 ? (
                <div style={{ textAlign:'center', padding:'32px 0' }}>
                  <p style={{ fontSize:44, margin:0 }}>🛒</p>
                  <p style={{ color:'#aaa', marginTop:8 }}>Your cart is empty</p>
                </div>
              ) : (
                cart.map((c) => (
                  <div key={c.itemId} style={S.cartRow}>
                    <img src={c.imageUrl} alt={c.name} style={S.cartThumb} />
                    <div style={S.cartInfo}>
                      <p style={S.cartName}>{c.name}</p>
                      <p style={S.cartPrice}>₹{(c.price * c.qty).toFixed(2)}</p>
                    </div>
                    <div style={S.cartQtyRow}>
                      <button style={S.cartQtyBtn} onClick={() => changeCartQty(c.itemId, -1)}>−</button>
                      <span style={S.cartQtyNum}>{c.qty}</span>
                      <button style={S.cartQtyBtn} onClick={() => changeCartQty(c.itemId, +1)}>+</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <>
                <div style={S.totalRow}>
                  <span style={S.totalLabel}>Total ({cartCount} item{cartCount !== 1 ? 's' : ''})</span>
                  <span style={S.totalValue}>₹{cartTotal.toFixed(2)}</span>
                </div>
                <button
                  style={{ ...S.placeOrderBtn, ...(placingOrder ? S.placeOrderBtnLoading : {}) }}
                  onClick={handlePlaceOrder}
                  disabled={placingOrder}
                >
                  {placingOrder ? (
                    <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
                      <span style={S.spinner} />
                      Placing Order…
                    </span>
                  ) : (
                    `Place Order  ·  ₹${cartTotal.toFixed(2)}`
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
           TABLE NUMBER BOTTOM SHEET
         ══════════════════════════════════════════════════════════ */}
      {showTableModal && (
        <div style={S.overlay} onClick={() => { setShowTableModal(false); setAfterTable(null); }}>
          <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={S.sheetHandle} />
            <div style={{ width:68, height:68, borderRadius:20, background:'#fff5f0', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12 }}>
              <span style={{ fontSize:36 }}>🪑</span>
            </div>
            <h2 style={{ ...S.sheetTitle, marginBottom:6 }}>Which table are you at?</h2>
            <p style={{ color:'#888', fontSize:14, marginBottom:20, textAlign:'center' }}>
              We'll bring your order right to your seat.
            </p>
            <input
              type="number"
              inputMode="numeric"
              placeholder="e.g. 7"
              value={tableInput}
              onChange={(e) => { setTableInput(e.target.value); setTableError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleTableConfirm()}
              style={{ ...S.tableInput, ...(tableError ? { borderColor:'#ff3b30' } : {}) }}
              autoFocus
            />
            {tableError && (
              <p style={{ color:'#ff3b30', fontSize:13, margin:'0 0 4px', alignSelf:'flex-start' }}>{tableError}</p>
            )}
            <div style={{ display:'flex', gap:10, width:'100%', marginTop:16 }}>
              <button
                style={{ flex:1, height:50, border:'2px solid #e8e8e8', borderRadius:14, background:'#fff', fontSize:15, fontWeight:600, color:'#555', cursor:'pointer' }}
                onClick={() => { setShowTableModal(false); setAfterTable(null); }}
              >Cancel</button>
              <button style={S.tableConfirmBtn} onClick={handleTableConfirm}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 3D MODAL ─────────────────────────────────────────────── */}
      {show3D && item?.modelUrl && (
        <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.92)', zIndex:1200, display:'flex', flexDirection:'column' }}>
          <div style={{ flex:1, display:'flex', justifyContent:'center', alignItems:'center' }}>
            <Model3DViewer camera-controls auto-rotate modelUrl={item.modelUrl} fullScreen onClose={() => setShow3D(false)} />
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────
const S = {

  headerCartBtn: {
    position:'relative', flexShrink:0, background:'#fff5f0', border:'none',
    borderRadius:12, width:42, height:42, fontSize:20, cursor:'pointer',
    display:'flex', alignItems:'center', justifyContent:'center',
  },
  headerCartBadge: {
    position:'absolute', top:3, right:3, background:'#f7431b', color:'#fff',
    fontSize:9, fontWeight:800, borderRadius:99, minWidth:16, height:16,
    padding:'0 3px', display:'flex', alignItems:'center', justifyContent:'center',
  },

  stickyBar: {
    position:'fixed', bottom:0, left:'50%', transform:'translateX(-50%)',
    width:'100%', maxWidth:480, display:'flex', alignItems:'center', gap:10,
    padding:'10px 14px', paddingBottom:'calc(10px + env(safe-area-inset-bottom))',
    background:'#fff', borderTop:'1px solid #f0f0f0',
    boxShadow:'0 -4px 24px rgba(0,0,0,0.08)', zIndex:500, boxSizing:'border-box',
  },

  qtyWrapper: { display:'flex', alignItems:'center', background:'#f5f5f5', borderRadius:14, padding:'4px 6px', flexShrink:0 },
  qtyBtn: { width:32, height:32, border:'none', background:'transparent', borderRadius:10, fontSize:20, fontWeight:600, color:'#222', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', WebkitTapHighlightColor:'transparent', lineHeight:1 },
  qtyNum: { minWidth:26, textAlign:'center', fontSize:16, fontWeight:700, color:'#111' },

  addBtn: {
    flex:1, height:50, border:'none', borderRadius:14,
    background:'linear-gradient(135deg,#ff6b35 0%,#f7431b 100%)',
    color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer',
    boxShadow:'0 4px 16px rgba(247,67,27,0.3)', transition:'background 0.3s',
    WebkitTapHighlightColor:'transparent',
  },
  addBtnFlash: {
    background:'linear-gradient(135deg,#27ae60 0%,#1e8449 100%)',
    boxShadow:'0 4px 16px rgba(39,174,96,0.3)',
  },

  viewCartBtn: {
    position:'relative', height:50, minWidth:54, border:'none', borderRadius:14,
    background:'#f3f3f3a0', color:'#fff', fontSize:20, cursor:'pointer',
    display:'flex', alignItems:'center', justifyContent:'center', gap:3,
    flexShrink:0, WebkitTapHighlightColor:'transparent',
  },
  viewCartBadge: {
    fontSize:12, fontWeight:800, background:'#f7431b', borderRadius:99,
    minWidth:20, height:20, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 5px',
  },

  toast: {
    position:'fixed', bottom:90, left:'50%', transform:'translateX(-50%)',
    background:'#1a1a1a', color:'#fff', borderRadius:50,
    padding:'10px 24px', fontSize:14, fontWeight:600,
    zIndex:800, whiteSpace:'nowrap', boxShadow:'0 6px 24px rgba(0,0,0,0.25)',
    animation:'toastIn 0.3s ease, toastOut 0.4s ease 2.6s forwards',
  },

  overlay: { position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.52)', zIndex:900, display:'flex', flexDirection:'column', justifyContent:'flex-end' },
  sheet: { background:'#fff', borderRadius:'22px 22px 0 0', padding:'16px 18px calc(28px + env(safe-area-inset-bottom))', maxHeight:'88vh', overflowY:'auto', display:'flex', flexDirection:'column', alignItems:'center' },
  sheetHandle: { width:40, height:4, borderRadius:99, background:'#e0e0e0', marginBottom:14 },
  sheetHeader: { display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', marginBottom:12 },
  sheetTitle: { margin:0, fontSize:18, fontWeight:800, color:'#111' },
  closeBtn: { background:'#f2f2f2', border:'none', borderRadius:99, width:32, height:32, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' },

  tableChip: { alignSelf:'flex-start', display:'flex', alignItems:'center', gap:8, background:'#fff5f0', borderRadius:99, padding:'6px 14px', fontSize:13, fontWeight:600, color:'#f7431b', marginBottom:12 },
  tableChipChange: { background:'none', border:'none', color:'#888', fontSize:12, fontWeight:600, cursor:'pointer', textDecoration:'underline' },

  cartList: { width:'100%', display:'flex', flexDirection:'column', gap:10, marginBottom:8 },
  cartRow: { display:'flex', alignItems:'center', gap:12, padding:'10px 12px', background:'#fafafa', borderRadius:14 },
  cartThumb: { width:52, height:52, borderRadius:10, objectFit:'cover', flexShrink:0 },
  cartInfo: { flex:1, minWidth:0 },
  cartName: { margin:0, fontSize:14, fontWeight:700, color:'#111', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },
  cartPrice: { margin:'3px 0 0', fontSize:13, color:'#f7431b', fontWeight:700 },
  cartQtyRow: { display:'flex', alignItems:'center', background:'#efefef', borderRadius:10, padding:'2px 4px' },
  cartQtyBtn: { width:28, height:28, border:'none', background:'transparent', fontSize:18, fontWeight:600, color:'#222', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' },
  cartQtyNum: { minWidth:22, textAlign:'center', fontSize:14, fontWeight:700, color:'#111' },

  totalRow: { display:'flex', justifyContent:'space-between', alignItems:'center', width:'100%', padding:'12px 4px', borderTop:'1px solid #f0f0f0', marginTop:4 },
  totalLabel: { fontSize:15, color:'#666', fontWeight:600 },
  totalValue: { fontSize:20, fontWeight:800, color:'#111' },
  placeOrderBtn: { width:'100%', height:54, border:'none', borderRadius:16, marginTop:12, background:'linear-gradient(135deg,#ff6b35 0%,#f7431b 100%)', color:'#fff', fontSize:16, fontWeight:800, cursor:'pointer', boxShadow:'0 6px 20px rgba(247,67,27,0.35)', WebkitTapHighlightColor:'transparent', transition:'opacity 0.2s' },
  placeOrderBtnLoading: { opacity:0.6, cursor:'not-allowed' },
  spinner: { width:18, height:18, border:'3px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' },

  tableInput: { width:'100%', height:56, border:'2px solid #e8e8e8', borderRadius:14, fontSize:26, fontWeight:700, textAlign:'center', color:'#111', outline:'none', boxSizing:'border-box', background:'#fafafa', marginBottom:4, transition:'border-color 0.2s' },
  tableConfirmBtn: { flex:2, height:50, border:'none', borderRadius:14, background:'linear-gradient(135deg,#ff6b35 0%,#f7431b 100%)', color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer', boxShadow:'0 4px 14px rgba(247,67,27,0.3)' },

  upgradeBanner: {
    margin:'16px 0 8px', padding:'16px',
    background:'linear-gradient(135deg,#fffbeb,#fef3c7)',
    borderRadius:16, border:'1.5px solid #fde68a',
    display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
  },
  upgradeBannerLeft: { display:'flex', alignItems:'flex-start', gap:12, flex:1 },
  upgradeBannerTitle: { margin:0, fontSize:14, fontWeight:800, color:'#92400e' },
  upgradeBannerSub: { margin:'4px 0 0', fontSize:12, color:'#a16207', lineHeight:1.4 },
  upgradeBannerBtn: {
    flexShrink:0, height:38, padding:'0 16px', border:'none', borderRadius:99,
    background:'linear-gradient(135deg,#f59e0b,#d97706)', color:'#fff',
    fontSize:13, fontWeight:800, cursor:'pointer',
    boxShadow:'0 4px 12px rgba(245,158,11,0.4)', whiteSpace:'nowrap',
  },
};

/*
  Add to itemdetails.css:

  @keyframes toastIn {
    from { opacity:0; transform:translateX(-50%) translateY(10px); }
    to   { opacity:1; transform:translateX(-50%) translateY(0); }
  }
  @keyframes toastOut {
    from { opacity:1; }
    to   { opacity:0; transform:translateX(-50%) translateY(6px); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
*/